
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { LoaderIcon } from "lucide-react";
import { Link } from "react-router-dom";
import ResetPasswordForm from "./ResetPasswordForm";

const MAX_NAME_CHARACTERS = 25;

interface AuthFormProps {
  type: 'signin' | 'signup';
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export default function AuthForm({ type, onClose, onSuccess, onError }: AuthFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  if (showResetPassword) {
    return (
      <ResetPasswordForm 
        onClose={onClose} 
        onBack={() => setShowResetPassword(false)} 
      />
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    
    // For signup: require at least one name with 3+ characters
    if (type === "signup") {
      const firstNameValid = trimmedFirstName.length >= 3;
      const lastNameValid = trimmedLastName.length >= 3;
      
      if (!firstNameValid && !lastNameValid) {
        const errorMessage = t('profile.validation.nameRequired');
        toast({
          title: t("auth.error") || "Error",
          description: errorMessage,
          variant: "destructive",
        });
        if (onError) onError(errorMessage);
        return;
      }
    }
    
    if (!email || !password) {
      const errorMessage = t('auth.fillAllFields');
      toast({
        title: t("auth.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      if (onError) onError(errorMessage);
      return;
    }
    
    try {
      setLoading(true);

      if (type === "signup") {
        // Check for duplicate full names before signup
        const { data: existingProfiles } = await supabase
          .from('profiles')
          .select('first_name, last_name');
        
        if (existingProfiles) {
          const newFullName = `${trimmedFirstName} ${trimmedLastName}`.trim().toLowerCase();
          const nameExists = existingProfiles.some(profile => {
            const existingFullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim().toLowerCase();
            return existingFullName === newFullName && newFullName.length > 0;
          });
          
          if (nameExists) {
            toast({
              title: t("auth.error") || "Error",
              description: t('profile.validation.nameTaken'),
              variant: "destructive",
            });
            if (onError) onError(t('profile.validation.nameTaken'));
            setLoading(false);
            return;
          }
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: "https://goodvote.ge/email-confirmation",
            data: {
              first_name: trimmedFirstName,
              last_name: trimmedLastName
            }
          }
        });

        if (error) throw error;
        
        toast({
          title: t("auth.success") || "Success",
          description: t('auth.accountCreated'),
        });
        
        if (onSuccess) onSuccess();
        
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        toast({
          title: t("auth.success") || "Success",
          description: t('auth.loggedIn'),
        });
        
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error: any) {
      const errorMessage = error.message || t('auth.failed');
      toast({
        title: t("auth.error") || "Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const isSignIn = type === 'signin';
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isSignIn && (
        <>
          <div>
            <label htmlFor={`${type}-first-name`} className="block text-sm font-medium mb-1">
              {t('auth.firstName')} <span className="text-red-500">*</span>
            </label>
            <Input
              id={`${type}-first-name`}
              type="text"
              value={firstName}
              onChange={(e) => {
                const newFirstName = e.target.value;
                const combinedLength = newFirstName.length + lastName.length;
                if (combinedLength <= MAX_NAME_CHARACTERS) {
                  setFirstName(newFirstName);
                }
              }}
              required={!isSignIn}
              placeholder="John"
            />
          </div>
          
          <div>
            <label htmlFor={`${type}-last-name`} className="block text-sm font-medium mb-1">
              {t('auth.lastName')} <span className="text-red-500">*</span>
            </label>
            <Input
              id={`${type}-last-name`}
              type="text"
              value={lastName}
              onChange={(e) => {
                const newLastName = e.target.value;
                const combinedLength = firstName.length + newLastName.length;
                if (combinedLength <= MAX_NAME_CHARACTERS) {
                  setLastName(newLastName);
                }
              }}
              required={!isSignIn}
              placeholder="Doe"
            />
            <div className="text-xs text-muted-foreground text-right mt-1">
              {firstName.length + lastName.length}/{MAX_NAME_CHARACTERS} {t('profile.characters')}
            </div>
          </div>
        </>
      )}
      
      <div>
        <label htmlFor={`${type}-email`} className="block text-sm font-medium mb-1">
          {t('auth.email')}
        </label>
        <Input
          id={`${type}-email`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
        />
      </div>
      
      <div>
        <label htmlFor={`${type}-password`} className="block text-sm font-medium mb-1">
          {t('auth.password')}
        </label>
        <Input
          id={`${type}-password`}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          pattern="[A-Za-z0-9]{8,}"
          autoComplete={isSignIn ? "current-password" : "new-password"}
          placeholder="••••••••"
        />
        {!isSignIn && (
          <p className="text-xs text-muted-foreground mt-1">
            {t('auth.passwordHint')}
          </p>
        )}
      </div>
      
      {isSignIn && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowResetPassword(true)}
            className="text-sm text-primary hover:text-primary/80 underline"
          >
            {t("auth.forgotPassword")}
          </button>
        </div>
      )}
      
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? (
          <>
            <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
            {isSignIn ? t("auth.signingIn") : t("auth.signingUp")}
          </>
        ) : (
          isSignIn ? t("auth.signIn") : t("nav.signup")
        )}
      </Button>

      {/* Agreement text */}
      <div className="text-center text-xs text-muted-foreground mt-4">
        {isSignIn ? (
          <p>
            {t('auth.signInAgreement').split('Terms of Service').map((part, index) => (
              index === 0 ? (
                <span key={index}>
                  {part}
                  <Link 
                    to="/terms" 
                    className="text-primary underline hover:text-primary/80"
                    onClick={onClose}
                  >
                    Terms of Service
                  </Link>
                </span>
              ) : (
                <span key={index}>
                  {part.split('Privacy Policy').map((subPart, subIndex) => (
                    subIndex === 0 ? (
                      <span key={subIndex}>
                        {subPart}
                        <Link 
                          to="/privacy" 
                          className="text-primary underline hover:text-primary/80"
                          onClick={onClose}
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    ) : (
                      subPart
                    )
                  ))}
                </span>
              )
            ))}
          </p>
        ) : (
          <p>
            {t('auth.signUpAgreement').split('Terms of Service').map((part, index) => (
              index === 0 ? (
                <span key={index}>
                  {part}
                  <Link 
                    to="/terms" 
                    className="text-primary underline hover:text-primary/80"
                    onClick={onClose}
                  >
                    Terms of Service
                  </Link>
                </span>
              ) : (
                <span key={index}>
                  {part.split('Privacy Policy').map((subPart, subIndex) => (
                    subIndex === 0 ? (
                      <span key={subIndex}>
                        {subPart}
                        <Link 
                          to="/privacy" 
                          className="text-primary underline hover:text-primary/80"
                          onClick={onClose}
                        >
                          Privacy Policy
                        </Link>
                      </span>
                    ) : (
                      subPart
                    )
                  ))}
                </span>
              )
            ))}
          </p>
        )}
      </div>
    </form>
  );
}
